import produce from 'immer';
import { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { LayoutContext } from '../../contexts/layout';
import { ModalContext } from '../../contexts/modal';
import { ILayoutWithOwner, ILayoutWithRowId } from '../../factories/layout-factory';
import { pushFormFieldValidationResult, setFormStateMode } from '../../redux/actions/forms';
import { formFieldLayoutsSelector, getEditedFormValues, getFormValues } from '../../redux/selectors/forms';
import { layoutStateSelector } from '../../redux/selectors/layouts';
import { IActionLayout } from '../../types/layout/action';
import { TFormValues } from '../../types/redux/forms';
import { useFormValidation } from '../form/useFormValidation';
import useRest from '../useRest';
import useLayout from '../layout/useLayout';
import useMenu from '../menu/useMenu';
import { TFormFieldLayout } from '../../types/layout/form';
import { useUploader } from '../uploader/useUploader';

export const useActionButton = ({
    action, additionalFormData, target, idOwner, closeModalAfterExecute, rowId, nextAction, onlyEditedFields,
}: IActionLayout & ILayoutWithOwner & ILayoutWithRowId) => {
    const { layoutId } = useContext(LayoutContext);
    const modalContext = useContext(ModalContext);

    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();
    const formData = useSelector(getFormValues(layoutId));
    const editedFormData = useSelector(getEditedFormValues(layoutId));
    const layout = useSelector(layoutStateSelector(layoutId));
    const rest = useRest();
    const { objectId } = useParams();
    const navigate = useNavigate();
    const { updateLayout } = useLayout(layoutId, true);
    const menu = useMenu(true);
    const fieldLayouts = useSelector(formFieldLayoutsSelector(layoutId));
    const { saveTextAsFile } = useUploader({ dataLink: '/files/list/data' } as any, true);

    const formValidation = useFormValidation();

    const executeConstantAction = () => {
        switch (action) {
            case 'form-action-mode-edit': {
                dispatch(setFormStateMode(layoutId, 'edit'));
                return true;
            }
            default: {
                return false;
            }
        }
    };

    const processFieldDepengingsOnFiles = async (fieldName: string, fieldLayout: TFormFieldLayout) => {
        switch (fieldLayout.type) {
            case 'html-editor': {
                if (fieldLayout.mode === 'file' && typeof editedFormData[fieldName] === 'string') {
                    await saveTextAsFile(`[html-editor]-${fieldName}.txt`, editedFormData[fieldName]);
                }
                break;
            }
        }
    };

    const processFieldsDepengingsOnFiles = async () => {
        if (fieldLayouts instanceof Object) {
            const keys = Object.keys(fieldLayouts);
            for (let i = 0; i < keys.length; i += 1) {
                // eslint-disable-next-line no-await-in-loop
                await processFieldDepengingsOnFiles(keys[i], fieldLayouts[keys[i]]);
            }
        }
    };

    const executeFormAction = async () => {
        setLoading(true);

        formValidation.validate();

        const url = new URL(window.location.href);
        let objectName = action;

        if (typeof idOwner === 'undefined' && typeof objectId === 'string') {
            url.searchParams.append('id', objectId);
        }

        if (typeof rowId !== 'undefined' && typeof rowId.toString === 'function') {
            url.searchParams.append('id', rowId.toString());
        }

        objectName += url.search;

        let formDataForSend = typeof objectId === 'string' ? editedFormData : formData;

        if (typeof objectId === 'string') {
            formDataForSend = typeof onlyEditedFields === 'undefined' || onlyEditedFields ? editedFormData : formData;
        } else {
            formDataForSend = formData;
        }

        const data = produce(formDataForSend, (draft: TFormValues) => {
            if (additionalFormData && typeof additionalFormData === 'object') {
                Object.assign(draft, additionalFormData);
            }
            if (typeof idOwner !== 'undefined') {
                draft.idOwner = idOwner;
            }
            if (typeof objectId === 'undefined' && typeof layout?.id !== 'undefined') {
                draft.id = layout.id;
            }

            if (fieldLayouts instanceof Object) {
                Object.keys(fieldLayouts).forEach((key) => {
                    const fieldLayout = fieldLayouts[key];
                    switch (fieldLayout.type) {
                        case 'html-editor': {
                            if (fieldLayout.mode === 'file') {
                                delete draft[fieldLayout.dataIndex];
                                break;
                            }
                        }
                    }
                });
            }

            return draft;
        });

        const result = await rest.fetch({
            method: 'post',
            objectName,
            data,
        });

        if (!result.isError) {
            await processFieldsDepengingsOnFiles();

            if (typeof result.response?.data?.redirect === 'string') {
                navigate(result.response.data.redirect);
            } else if (closeModalAfterExecute) {
                modalContext.hide();
            } else if (nextAction instanceof Object) {
                if (nextAction.updateLayout) {
                    updateLayout();
                }
                if (nextAction.updateMenu) {
                    menu.getMenuData();
                }
                if (typeof nextAction.navigate === 'string') {
                    navigate(nextAction.navigate.replace('{objectId}', result.response?.data.id));
                }
            }
        } else if (typeof result.response?.data?.error === 'object') {
            if (result.response.data.error?.fields && typeof result.response.data.error?.fields === 'object') {
                const { fields } = result.response.data.error;

                const keys = Object.keys(fields);

                keys.forEach((key) => {
                    dispatch(pushFormFieldValidationResult(layoutId, key, {
                        isValid: false,
                        message: fields[key],
                    }));
                });
            }
        }

        setLoading(false);
    };

    const executeAction = async () => {
        const constantExecuted = executeConstantAction();

        if (!constantExecuted) {
            if (target === 'form') {
                executeFormAction();
            }
        }
    };

    return {
        executeAction,
        loading,
    };
};
